import React from 'react';
import { useTranslation } from 'core/i18n';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { ModalDialog, TooltipedTextComponent } from 'commons/components';
import { PopupProps } from 'react-popup-manager';
import { PredefinedComment } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/predefined-comments.model';
import { getModalTexts } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-list/components/predefined-comments-active-confirmation-modal/predefined-comments-active-confirmation-modal.business';
import { PredefinedCommentActiveConfirmModalAction } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-list/settings-devices-predefined-comments-list.model';
import { sortAndJoin } from 'commons/utils/sort.utils';

export interface PredefinedCommentsActiveConfirmationModalComponentProps extends PopupProps {
    actionFn: (ids: string[]) => Promise<void>;
    predefinedComments: PredefinedComment[];
    action: PredefinedCommentActiveConfirmModalAction;
}

export const PredefinedCommentsActiveConfirmationModalComponent: React.FC<
    PredefinedCommentsActiveConfirmationModalComponentProps
> = (props) => {
    const { onClose, actionFn, isOpen, predefinedComments, action } = props;
    const [isLoading, setIsLoading] = React.useState(false);
    const { t } = useTranslation();

    const texts = getModalTexts(action, predefinedComments);

    const handleAction = () => {
        setIsLoading(true);
        actionFn(predefinedComments.map((pdc) => pdc.id))
            .then(onClose?.())
            .finally(() => setIsLoading(false));
    };

    const getFooterOptions = (
        <PocButton
            id="predefined-comments-active-status-modal-confirm"
            key="predefined-comments-active-status-modal-confirm"
            onClick={handleAction}
            disabled={isLoading}
            data-testid="predefined-comments-active-status-modal-confirm"
        >
            {isLoading ? <PocProgressSpinner size="xSmall" style={{ display: 'block' }} /> : t(texts.buttonText)}
        </PocButton>
    );

    const getHeaderFragment = () =>
        predefinedComments.length === 1 ? (
            <PocTypography variant="button" data-testid="active-status-modal-title-single">
                {t(texts.headerText, {
                    name: predefinedComments[0].commentText,
                })}
            </PocTypography>
        ) : (
            <TooltipedTextComponent
                i18nKey={texts.headerText}
                tooltipText={sortAndJoin(predefinedComments.map((pdc) => pdc.commentText))}
                i18nValues={{ number: predefinedComments.length }}
                tooltipAnchorClassname="blue-tooltip-span"
                showTooltipAlways
            />
        );

    return (
        <ModalDialog title={getHeaderFragment()} isOpen={isOpen} onClose={onClose} footerOptions={getFooterOptions}>
            <PocTypography variant="body2" data-testid="activate-modal-body">
                {t(texts.bodyText)}
            </PocTypography>
        </ModalDialog>
    );
};
